import { createDebug } from '@/utils/debug'
import { isDesktopOfficeHost } from '@/services/office/helpers'

// AHA-43111: the read-view bridge for PowerPoint Online, kept isolated from services/office
// because it is the ONE binding store that is web-slideshow-specific. Starting Slide Show on
// the web spins up a BRAND-NEW session where PowerPoint.run (the Rich API) throws
// `RichApi.Error` and document.settings come up empty — so neither the host-slide tag nor the
// last-selected tag can be read, and the presenting cold-load falls through to the presentation
// list. localStorage is origin-scoped and DOES survive the slideshow reload, and document.url is
// a Common-API property that still works in the read view, so we bridge the current binding
// through localStorage keyed by the document URL.
//
// Keying by document is essential: an un-keyed value would leak across every .pptx opened in
// this browser (the exact reason the cross-frame store in services/office is a presentation TAG,
// not localStorage), so presenting document B would resume document A's binding. Every entry
// point is web-only via isPptOnlineAddin(): a no-op on desktop, which resolves from baked
// settings and never hits this path.
//
// STORES ONLY — durable bindings, written in the edit session and read back in the show. The
// live "which slide is on screen" channel that the show broadcasts between frames is a different
// concern on the same transport and lives in ppt-online-current-slide; it reuses the gate and the
// document-scoped key builder below.
//
// Toggle logging with: localStorage.debug = 'aha:ppt:online-binding' (or 'aha:ppt:*').
const log = createDebug('ppt:online-binding')

// Resolved lazily rather than via a static `import`, for the same reason debug.js does it:
// ppt-online.utils imports the store, the store's presentation module imports services/office,
// and services/office imports this file — a static import here closes that cycle. Every call
// site below runs at runtime, long after every module has finished evaluating, so a lazy
// require is safe where a static import would leave the binding undefined at init time.
// Exported so ppt-online-current-slide can reuse it rather than repeat the lazy-require dance;
// that module sits behind the same cycle and needs the same gate.
let isPptOnlineAddinFn = null
export const isPptOnlineAddin = () => {
  if (!isPptOnlineAddinFn) {
    try {
      // eslint-disable-next-line global-require
      isPptOnlineAddinFn = require('@/utils/ppt-online.utils').isPptOnlineAddin
    } catch (error) {
      isPptOnlineAddinFn = () => false
    }
  }
  return isPptOnlineAddinFn()
}

// Every key this add-in puts in localStorage is scoped by document.url — a Common-API property
// that survives into the Slide Show session and differs per .pptx. Un-scoped keys would leak
// across every deck opened in this browser, resolving document B to document A's binding.
// Shared with ppt-online-current-slide and ppt-show-owner-claim.
//
// AHA-43274: the DESKTOP host exposes no url until the deck has been saved to a path —
// slide-binding-history records the same weakness for the same reason — so every key built here
// was null on PC. The show-owner claim rode on that: nobody published, every read came back null,
// and the auto-stop veto the claim exists to run could never fire, ending a live quiz on every
// handover. Desktop therefore falls back to an un-scoped key rather than to nothing.
//
// The test for it is POSITIVE — isDesktopOfficeHost, which reads window.Office.context.platform —
// rather than `!isPptOnlineAddin()`, which was the first cut and is not the same question.
// isPptOnlineAddin is `platform === OFFICE_ONLINE && canUsePptOnline`, so false there also covers
// the plain web app, a web frame before captureOfficePlatform has committed the platform, and web
// with the rollout flag off; all three would have been handed the un-scoped key, and the only thing
// standing between them and it was an assumption about Office boot ordering — the same class of
// assumption this fallback exists to repair on desktop.
//
// It also keeps the lazy require out of this path, which matters for a reason beyond the import
// chain: that helper caches `() => false` PERMANENTLY if the require ever throws. Under the old
// test a failed require therefore meant "use an un-scoped key", inverting the failure direction
// from fail-safe to fail-unsafe. isDesktopOfficeHost has no such fallback to get wrong — an
// unknown platform is simply not desktop, and the key stays null.
//
// What the fallback costs, and why it is contained:
//   - The web NEVER reaches it. document.url is always present there, and a host that is not PC or
//     Mac cannot answer the test above, so the one case where an un-scoped key would genuinely be
//     unsafe returns null on both counts.
//   - Every other consumer — both stores in this file and both in ppt-online-current-slide — gates
//     on isPptOnlineAddin() before asking for a key, so on desktop they never get this far. The
//     show-owner claim is the only caller that can hold the fallback key.
//   - That claim is bounded to CLAIM_MAX_AGE_MS and only read during a live Slide Show, so the
//     whole exposure is two decks PRESENTED at the same moment on one machine — and what a
//     cross-deck claim produces there is a quiz left running, not one ended under its presenter.
const UNSCOPED_DESKTOP_SCOPE = 'desktop'

export const documentScopedKey = (prefix) => {
  const url = window.Office?.context?.document?.url
  if (url) return `${prefix}${url}`
  if (isDesktopOfficeHost()) {
    log('no document.url -> desktop fallback key (un-scoped)')
    return `${prefix}${UNSCOPED_DESKTOP_SCOPE}`
  }
  log('no document.url and not a desktop host -> skipping (cannot key safely)')
  return null
}

const KEY_PREFIX = 'aha:ppt:online-binding:'

const getBindingKey = () => documentScopedKey(KEY_PREFIX)

// `value` is the same {presentationId, slideId} / {activePresentationId} shape as dataSlidePPT.
// A falsy presentationId (the unbind case) clears the entry rather than storing a dead binding.
export const savePptOnlineBinding = (value) => {
  if (!isPptOnlineAddin()) return
  const key = getBindingKey()
  if (!key) return
  try {
    const presentationId = value && (value.activePresentationId || value.presentationId)
    if (!presentationId) {
      localStorage.removeItem(key)
      log('cleared |', key)
      return
    }
    const slideId = (value && value.slideId) || null
    localStorage.setItem(key, JSON.stringify({ presentationId, slideId }))
    log('saved |', key, '| presentationId =', presentationId, '| slideId =', slideId)
  } catch (error) {
    log('save failed', error)
  }
}

// Returns { presentationId, slideId } or null. slideId may be null (whole-presentation binding).
export const loadPptOnlineBinding = () => {
  if (!isPptOnlineAddin()) return null
  const key = getBindingKey()
  if (!key) return null
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    return parsed && parsed.presentationId ? parsed : null
  } catch (error) {
    log('load failed', error)
    return null
  }
}

// AHA-43111 per-slide read-view bridge. The doc-URL binding above holds ONE document-wide
// value, so a deck with several DISTINCT AhaSlides slides collapses to a single binding and
// the Slide Show resolves every slide to it. This second store is keyed by the slide's
// Common-API id (getSelectedDataAsync(SlideRange).id) so each physical slide carries its own
// binding — the probe confirmed that id is IDENTICAL for a given slide across the edit session
// (where we write) and the Slide Show session (where we read). Still document-scoped: the
// Common-API id (256, 257, …) restarts per .pptx, so an un-namespaced key would collide across
// documents opened in this browser.
const COMMON_KEY_PREFIX = 'aha:ppt:common-slide:'

const getCommonSlideKey = (commonSlideId) => {
  if (commonSlideId === null || commonSlideId === undefined || commonSlideId === '') {
    log('no common slide id -> skipping (cannot key)')
    return null
  }
  const documentKey = documentScopedKey(COMMON_KEY_PREFIX)
  return documentKey && `${documentKey}::${commonSlideId}`
}

// `value` is the dataSlidePPT shape { presentationId, slideId }. Only a complete single-slide
// binding is stored; a missing presentationId/slideId (the unbind case, or a whole-presentation
// binding that has no single slide) CLEARS the entry so the Slide Show never resolves a slide
// the binding no longer points at.
export const savePptCommonSlideBinding = (commonSlideId, value) => {
  if (!isPptOnlineAddin()) {
    // Symmetric with the load side: a web frame writing during an insert before canUsePptOnline
    // has resolved would silently skip, and the only visible symptom would be an empty bridge
    // several minutes later in the Slide Show.
    log('save common-slide skipped | not a PowerPoint Online add-in (or flag not ready) | id =', commonSlideId)
    return
  }
  const key = getCommonSlideKey(commonSlideId)
  if (!key) return
  try {
    const presentationId = value && value.presentationId
    const slideId = value && value.slideId
    if (!presentationId || !slideId) {
      localStorage.removeItem(key)
      log('cleared common-slide |', key)
      return
    }
    localStorage.setItem(key, JSON.stringify({ presentationId, slideId }))
    log('saved common-slide |', key, '| presentationId =', presentationId, '| slideId =', slideId)
  } catch (error) {
    log('save common-slide failed', error)
  }
}

// Returns { presentationId, slideId } for the given Common-API slide id, or null.
export const loadPptCommonSlideBinding = (commonSlideId) => {
  if (!isPptOnlineAddin()) {
    // Worth logging: off-web this is a no-op, but so is a web frame whose canUsePptOnline flag
    // has not resolved yet — and in the Slide Show that reads as "the bridge is empty".
    log('load common-slide skipped | not a PowerPoint Online add-in (or flag not ready)')
    return null
  }
  const key = getCommonSlideKey(commonSlideId)
  if (!key) return null
  try {
    const raw = localStorage.getItem(key)
    if (!raw) {
      log('MISS | looked for', key)
      return null
    }
    log('HIT |', key, '=>', raw)
    const parsed = JSON.parse(raw)
    return parsed && parsed.presentationId ? parsed : null
  } catch (error) {
    log('load common-slide failed', error)
    return null
  }
}
